import { Boundaries } from "@planzone/core";
import {
  Id,
  Identifiable,
  List,
  OptimizedGantt,
  ProjectElementType,
  RankedElement,
} from "@planzone/dto";
import { Event, events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { reduceOptimizedGantt } from "../../common/optimizedGantt";
import { MappedElement, Selector, State } from "./types";
import { createSelector } from "./selector";
import {
  eventToProjectElement,
  listToProjectElement,
  matchRequest,
  milestoneToProjectElement,
  taskToProjectElement,
} from "./utils";

const initialState: State = [];

function handleRequest(
  state: State,
  { filter, sort, size, page }: Boundaries["retrieveOptimizedGantt"]["request"],
): State {
  const index = matchRequest(state, filter, sort, size);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToPending() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          filter,
          sort,
          size,
          currentPage: page,
          gantt: [],
          canLoadMore: true,
          request: setRequestStateToPending(),
        },
      ];
}

function handleSuccess(
  state: State,
  {
    gantt,
    filter,
    sort,
    size,
    page,
  }: Boundaries["retrieveOptimizedGantt"]["success"],
): State {
  const index = matchRequest(state, filter, sort, size);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          canLoadMore: gantt.length >= size,
          currentPage: page + 1,
          gantt:
            page === 0
              ? gantt
              : [
                  ...state[index].gantt.slice(0, page * size),
                  ...gantt,
                  ...state[index].gantt.slice(page * size + size),
                ],
          request: setRequestStateToSuccess(),
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          sort,
          size,
          filter,
          canLoadMore: gantt.length >= size,
          currentPage: page + 1,
          gantt: gantt,
          request: setRequestStateToSuccess(),
        },
      ];
}

function handleFailure(
  state: State,
  { filter, sort, size }: Boundaries["retrieveOptimizedGantt"]["failure"],
): State {
  const index = matchRequest(state, filter, sort, size);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToFailure() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          filter,
          sort,
          size,
          currentPage: 0,
          gantt: [],
          canLoadMore: true,
          request: setRequestStateToFailure(),
        },
      ];
}

function resolveMinIndex(
  foundMin: number | null,
  rangeMin: RankedElement<any> | null,
  el: OptimizedGantt[number],
  index: number,
  nextParentList: Identifiable<List>,
) {
  return foundMin !== null
    ? foundMin
    : !!rangeMin && el.uuid === rangeMin.uuid
      ? index
      : rangeMin === null && el.uuid === nextParentList.uuid
        ? index
        : null;
}

function resolveMaxIndex(
  foundMax: number | null,
  rangeMax: RankedElement<any> | null,
  el: OptimizedGantt[number],
  index: number,
) {
  return foundMax !== null
    ? foundMax
    : !!rangeMax && el.uuid === rangeMax.uuid
      ? index
      : null;
}

function resolveProjectEl(
  foundEl: OptimizedGantt[number] | null,
  el: OptimizedGantt[number],
  projectElementId: Id,
) {
  return foundEl !== null ? foundEl : el.uuid === projectElementId ? el : null;
}

function resolveIndex(
  foundIndex: number | null,
  el: OptimizedGantt[number],
  projectElementId: Id,
  index: number,
) {
  return foundIndex !== null
    ? foundIndex
    : el.uuid === projectElementId
      ? index
      : null;
}

function resolveDepth(
  foundDepth: number | null,
  el: OptimizedGantt[number],
  nextParentList: Identifiable<List>,
) {
  return foundDepth !== null
    ? foundDepth
    : el.type === ProjectElementType.PROJECT &&
        el.activityRootUuid === nextParentList.uuid
      ? 0
      : el.uuid === nextParentList.uuid
        ? el.depth + 1
        : null;
}

function resolveListEndIndex(
  foundIndex: number | null,
  foundEl: OptimizedGantt[number] | null,
  el: OptimizedGantt[number],
  currentIndex: number,
  elementsLength: number,
) {
  if (foundIndex !== null) return foundIndex;

  if (foundEl !== null && foundEl.type === ProjectElementType.LIST) {
    return el.depth <= foundEl.depth
      ? currentIndex - 1
      : currentIndex === elementsLength - 1
        ? currentIndex
        : null;
  }

  return null;
}

function handleMoveElement(
  state: State,
  {
    projectElementId,
    nextParentList,
    range,
  }: Boundaries["moveListElement"]["success"],
): State {
  return state.map((req) => {
    const [
      minIndex,
      maxIndex,
      projectElement,
      currentIndex,
      nextDepth,
      listEndIndex,
      minListEndIndex,
    ] = req.gantt.reduce<
      [
        number | null,
        number | null,
        OptimizedGantt[number] | null,
        number | null,
        number | null,
        number | null,
        number | null,
      ]
    >(
      (
        [
          minIndex,
          maxIndex,
          projectElement,
          currentIndex,
          nextDepth,
          listEndIndex,
          minListEndIndex,
        ],
        el,
        index,
      ) => [
        resolveMinIndex(minIndex, range.min, el, index, nextParentList),
        resolveMaxIndex(maxIndex, range.max, el, index),
        resolveProjectEl(projectElement, el, projectElementId),
        resolveIndex(currentIndex, el, projectElementId, index),
        resolveDepth(nextDepth, el, nextParentList),
        resolveListEndIndex(
          listEndIndex,
          projectElement,
          el,
          index,
          req.gantt.length,
        ),
        resolveListEndIndex(
          minListEndIndex,
          minIndex && req.gantt[minIndex] ? req.gantt[minIndex] : null,
          el,
          index,
          req.gantt.length,
        ),
      ],
      [null, null, null, null, null, null, null],
    ) as [
      number | null,
      number | null,
      OptimizedGantt[number] | null,
      number | null,
      number | null,
      number | null,
      number | null,
    ];

    if (
      (minIndex === null && maxIndex === null) ||
      projectElement === null ||
      currentIndex === null
    )
      return req;

    const resolvedMin =
      !!range.min &&
      range.min.type === ProjectElementType.LIST &&
      minListEndIndex !== null
        ? minListEndIndex
        : minIndex !== null
          ? minIndex
          : (maxIndex as number) - 1 < 0
            ? -1
            : (maxIndex as number) - 1;
    const resolvedMax = maxIndex !== null ? maxIndex : (minIndex as number) + 1;
    const resolvedListEndIndex =
      listEndIndex !== null ? listEndIndex : currentIndex;
    const elementsToPlace = req.gantt
      .slice(currentIndex, resolvedListEndIndex + 1)
      .map((el, index) =>
        index === 0
          ? {
              ...projectElement,
              parentUuid: nextParentList.uuid,
              depth: nextDepth === null ? 0 : nextDepth,
            }
          : {
              ...el,
              depth:
                nextDepth === null
                  ? el.depth
                  : nextDepth + el.depth - projectElement.depth,
            },
      );
    const slicedGantt = [
      ...req.gantt.slice(0, currentIndex),
      ...req.gantt.slice(resolvedListEndIndex + 1),
    ];

    return {
      ...req,
      gantt:
        resolvedMin > currentIndex
          ? [
              ...slicedGantt.slice(0, resolvedMin - elementsToPlace.length + 1),
              ...elementsToPlace,
              ...slicedGantt.slice(resolvedMin - elementsToPlace.length + 1),
            ]
          : [
              ...slicedGantt.slice(0, resolvedMin + 1),
              ...elementsToPlace,
              ...slicedGantt.slice(resolvedMin + 1),
            ],
    };
  });
}

function handleRemoveList(
  state: State,
  list: Boundaries["removeList"]["success"],
): State {
  return state.map((req) => ({
    ...req,
    gantt: req.gantt.reduce<[OptimizedGantt, OptimizedGantt[number] | null]>(
      ([nextGantt, foundEl], projectElement) => [
        (!!foundEl && foundEl.depth < projectElement.depth) ||
        projectElement.uuid === list.uuid
          ? nextGantt
          : [...nextGantt, projectElement],
        foundEl !== null
          ? foundEl.depth >= projectElement.depth
            ? null
            : foundEl
          : projectElement.uuid === list.uuid
            ? projectElement
            : null,
      ],
      [[], null],
    )[0],
  }));
}

function sortByRank<T>(elements: RankedElement<T>[]): T[] {
  return [...elements].sort((current, next) =>
    current.rank < next.rank ? -1 : 1,
  );
}

function convertListToPE(
  list: Identifiable<List>,
  depth: number,
): OptimizedGantt {
  const listPe = listToProjectElement(list, (l) => ({ ...l, depth }));
  const children = sortByRank([
    ...list.subLists.map((element) => ({
      type: ProjectElementType.LIST,
      data: element,
      rank: element.activityKeyRank,
    })),
    ...list.tasks.map((element) => ({
      type: ProjectElementType.TASK,
      data: element,
      rank: element.activityKeyRank,
    })),
    ...list.milestones.map((element) => ({
      type: ProjectElementType.MILESTONE,
      data: element,
      rank: element.keyRank,
    })),
    ...list.events.map((element) => ({
      type: ProjectElementType.EVENT,
      data: element,
      rank: element.keyRank,
    })),
  ] as MappedElement[]);

  return [
    listPe,
    ...children.reduce<OptimizedGantt>(
      (acc, element) =>
        element.type === ProjectElementType.LIST
          ? [...acc, ...convertListToPE(element.data, depth + 1)]
          : element.type === ProjectElementType.TASK
            ? [
                ...acc,
                taskToProjectElement(element.data, (t) => ({
                  ...t,
                  depth: depth + 1,
                })),
              ]
            : element.type === ProjectElementType.MILESTONE
              ? [
                  ...acc,
                  milestoneToProjectElement(element.data, (m) => ({
                    ...m,
                    depth: depth + 1,
                  })),
                ]
              : element.type === ProjectElementType.EVENT
                ? [
                    ...acc,
                    eventToProjectElement(element.data, (e) => ({
                      ...e,
                      depth: depth + 1,
                    })),
                  ]
                : acc,
      [],
    ),
  ];
}

function handleDuplicateList(
  state: State,
  { list, duplicatedList }: Boundaries["duplicateList"]["success"],
): State {
  return state.map((req) => {
    const index = req.gantt.findIndex((element) => element.uuid === list.uuid);

    if (index === -1) return req;

    const convertedList = convertListToPE(
      duplicatedList,
      req.gantt[index].depth,
    );

    return {
      ...req,
      gantt:
        index !== -1
          ? [
              ...req.gantt.slice(0, index + convertedList.length),
              ...convertedList,
              ...req.gantt.slice(index + convertedList.length),
            ]
          : req.gantt,
    };
  });
}

function propagateEvent(state = initialState, event: Event): State {
  return state.map((req) => ({
    ...req,
    gantt: reduceOptimizedGantt(req.gantt, event),
  }));
}

const reduceState = resetState(
  initialState,
  createStateReducer(
    initialState,
    {
      [events.retrieveOptimizedGanttRequested]: handleRequest,
      [events.retrieveOptimizedGanttSucceeded]: handleSuccess,
      [events.retrieveOptimizedGanttFailed]: handleFailure,
      [events.moveListElementSucceeded]: handleMoveElement,
      [events.duplicateListSucceeded]: handleDuplicateList,
      [events.removeListSucceeded]: handleRemoveList,
      // [events.retrieveProjectGanttSucceeded]: handleRetrieveProjectGantt,
      // [events.retrieveZoneGanttSucceeded]: handleRetrieveZoneGantt,
    },
    propagateEvent,
  ),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
